import { useEffect, useMemo, useRef } from 'react';
import { usePermissions } from '@/features/auth';
import { useQuote } from '../context/QuoteContext';
import type { CustomerLinkContacts } from '../types/linkDelivery.types';
import { paymentReconfirmRequired } from '../utils/paymentAmountReconfirm';
import { customerLinkContacts, defaultLinkDelivery } from '../utils/paymentCollectionGate';
import { useInitiationPrecondition } from './useInitiationPrecondition';
import { useElemisCheckout, type ElemisCheckoutState } from './useElemisCheckout';
import { useCanSelfCollectPayment } from './useCanSelfCollectPayment';
import { useElemisCheckoutPendingToast } from './useElemisCheckoutPendingToast';
import { usePaymentCollectionGate, type PaymentCollectionGate } from './usePaymentCollectionGate';
import type { VehicleIdentityGate } from './useVehicleIdentityGate';

export interface UseQuotePaymentJourneyOptions {
    completeSale: () => Promise<void>;
    completingSale: boolean;
    assignmentsPending: boolean;
    identityGate: Pick<VehicleIdentityGate, 'allow' | 'resolvedIntent' | 'clearResolvedIntent'>;
}

export interface QuotePaymentJourney {
    elemisCheckout: ElemisCheckoutState;
    paymentCollectionGate: PaymentCollectionGate;
    handleContinueToPayment: () => void;
    continueBlockReason: string | null;
    canPressContinue: boolean;
    reconfirmRequired: boolean;
    customerName: string | undefined;
    contacts: CustomerLinkContacts;
    liveCheckoutHeld: boolean;
}

export function useQuotePaymentJourney(
    options: UseQuotePaymentJourneyOptions
): QuotePaymentJourney {
    const {
        completeSale,
        completingSale,
        assignmentsPending,
        identityGate,
    } = options;
    const { allow, resolvedIntent, clearResolvedIntent } = identityGate;
    const quote = useQuote();
    const { hasPermission } = usePermissions();

    const hasInitiatePermission = hasPermission({
        resource: 'agreement',
        action: 'initiate_application',
    });
    const selectedCustomer = quote.selectedCustomer;
    const customerEmail = selectedCustomer?.email;
    const customerMobile = selectedCustomer?.mobile_phone;
    const contacts = useMemo(
        () => customerLinkContacts({ email: customerEmail, mobile_phone: customerMobile }),
        [customerEmail, customerMobile]
    );
    const defaultDelivery = useMemo(() => defaultLinkDelivery(contacts), [contacts]);
    const beginInitiation = useInitiationPrecondition(quote.saveLane);
    const elemisCheckout = useElemisCheckout(
        quote.quoteId,
        quote.paymentSelection,
        quote.canCompleteQuote(),
        hasInitiatePermission,
        defaultDelivery,
        beginInitiation
    );
    useElemisCheckoutPendingToast({
        quoteId: quote.quoteId,
        isInitiating: elemisCheckout.isInitiating,
        isSlow: elemisCheckout.isSlow,
        isVerySlow: elemisCheckout.isVerySlow,
    });
    const liveCheckoutHeld =
        elemisCheckout.checkoutUrl !== null &&
        (elemisCheckout.outcome === null || elemisCheckout.outcome === 'completed');

    const canSelfCollect = useCanSelfCollectPayment(quote.selectedDealer?.id ?? null);
    const paymentCollectionGate = usePaymentCollectionGate({
        paymentSelection: quote.paymentSelection,
        canSelfCollect,
        canInitiate: hasInitiatePermission,
        liveCheckoutHeld,
        journey: elemisCheckout.journey,
        updatePaymentSelection: quote.updatePaymentSelection,
        saveLane: quote.saveLane,
        initiateCheckout: elemisCheckout.initiate,
        retryCheckout: elemisCheckout.retry,
        completeSale,
    });
    const { requestContinue } = paymentCollectionGate;
    const reconfirmRequired = paymentReconfirmRequired(
        quote.saveLane.lastError,
        quote.paymentSelection
    );

    const handleContinueToPayment = () => {
        if (!reconfirmRequired && allow('checkout')) requestContinue();
    };

    const intentDraftIdRef = useRef<string | null>(null);
    const { draftId } = quote;
    useEffect(() => {
        if (!resolvedIntent) {
            intentDraftIdRef.current = null;
            return;
        }
        intentDraftIdRef.current ??= draftId;
        // A held intent belongs to the draft it was resolved on; Start Over must not inherit it.
        if (intentDraftIdRef.current !== draftId) {
            clearResolvedIntent();
            return;
        }
        if (assignmentsPending) return;
        clearResolvedIntent();
        if (!reconfirmRequired) requestContinue();
    }, [resolvedIntent, draftId, assignmentsPending, reconfirmRequired, clearResolvedIntent, requestContinue]);

    const canCompleteQuotePermission = hasPermission({ resource: 'quote', action: 'edit' });
    const { mode } = paymentCollectionGate;
    const canCompleteDealerCollected =
        (mode.kind === 'choice' || mode.kind === 'dealer_only') &&
        mode.axis === 'full_payment' &&
        canCompleteQuotePermission;
    const canRouteToPayment = hasInitiatePermission || canCompleteDealerCollected;

    const continueBlockReason =
        quote.status === 'completed'
            ? null
            : !quote.quoteId
              ? 'Save the quote before starting the payment journey.'
              : !quote.canCompleteQuote()
                ? 'Complete every previous step before starting the payment journey.'
                : !canRouteToPayment
                  ? "You don't have permission to start the payment journey. Ask an administrator to grant it."
                  : null;

    const canPressContinue =
        !!quote.quoteId &&
        quote.canCompleteQuote() &&
        canRouteToPayment &&
        !elemisCheckout.isInitiating &&
        !completingSale &&
        !assignmentsPending &&
        !paymentCollectionGate.isBusy;

    const customerName = quote.selectedCustomer
        ? [
              quote.selectedCustomer.title,
              quote.selectedCustomer.forename,
              quote.selectedCustomer.surname,
          ]
              .filter(Boolean)
              .join(' ')
        : undefined;

    return {
        elemisCheckout,
        paymentCollectionGate,
        handleContinueToPayment,
        continueBlockReason,
        canPressContinue,
        reconfirmRequired,
        customerName,
        contacts,
        liveCheckoutHeld,
    };
}
